import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API_BASE, { withApiCredentials } from "../api";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";
import { getLocalizedRequiredConsentTexts } from "../content/legal/localizedConsentTexts";
import "../styles/components/LegalAcceptanceModal.css";

function getAcceptanceError(detail, t, localizedConsentTexts) {
  if (detail === "acceptTermsPrivacy_required") {
    return localizedConsentTexts.acceptTermsPrivacy.error;
  }

  if (detail === "acceptProfileVisibility_required") {
    return localizedConsentTexts.acceptProfileVisibility.error;
  }

  return t("legal_acceptance_save_failed");
}

function renderTermsPrivacyLabel(localizedConsentTexts, t) {
  const label = localizedConsentTexts.acceptTermsPrivacy.label;
  const labelParts = localizedConsentTexts.acceptTermsPrivacy.labelParts;
  const termsLabel = t("legal_document_terms_title");
  const privacyLabel = t("required_consent_privacy_label");

  if (labelParts) {
    return (
      <>
        {labelParts.beforeTerms}
        <Link to="/legal/terms">{labelParts.termsLabel}</Link>
        {labelParts.betweenLinks}
        <Link to="/legal/privacy">{labelParts.privacyLabel}</Link>
        {labelParts.afterPrivacy}
      </>
    );
  }

  if (!label.includes(termsLabel) || !label.includes(privacyLabel)) {
    return label;
  }

  const [beforeTerms, afterTerms] = label.split(termsLabel);
  const [betweenLinks, afterPrivacy] = afterTerms.split(privacyLabel);

  return (
    <>
      {beforeTerms}
      <Link to="/legal/terms">{termsLabel}</Link>
      {betweenLinks}
      <Link to="/legal/privacy">{privacyLabel}</Link>
      {afterPrivacy}
    </>
  );
}

export default function LegalAcceptanceModal({ user, onAccepted }) {
  const { t } = useTranslation();
  const location = useLocation();
  const localizedConsentTexts = getLocalizedRequiredConsentTexts(t);
  const [acceptTermsPrivacy, setAcceptTermsPrivacy] = useState(false);
  const [acceptProfileVisibility, setAcceptProfileVisibility] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isLegalRoute = location.pathname.startsWith("/legal/");

  if (!user?.requiresLegalAcceptance || isLegalRoute) {
    return null;
  }

  const handleSubmit = async () => {
    if (!acceptTermsPrivacy) {
      setError(localizedConsentTexts.acceptTermsPrivacy.error);
      return;
    }

    if (!acceptProfileVisibility) {
      setError(localizedConsentTexts.acceptProfileVisibility.error);
      return;
    }

    const token = getActiveAwareAuthToken(window);
    if (!token && !user?.id) {
      setError(t("legal_acceptance_session_error"));
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE}/auth/accept-legal`,
        withApiCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            acceptTermsPrivacy,
            acceptProfileVisibility,
          }),
        })
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(getAcceptanceError(data?.detail, t, localizedConsentTexts));
        return;
      }

      onAccepted?.(data);
    } catch (requestError) {
      console.error("Legal acceptance error:", requestError);
      setError(t("legal_acceptance_save_failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="legal-acceptance-overlay" role="presentation">
      <div
        className="legal-acceptance-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-acceptance-title"
      >
        <p className="legal-acceptance-eyebrow">
          {t("legal_acceptance_update")}
        </p>
        <h2 id="legal-acceptance-title" className="legal-acceptance-title">
          {t("legal_acceptance_title")}
        </h2>
        <p className="legal-acceptance-copy">
          {t("legal_acceptance_intro")}
        </p>

        <div
          className="legal-acceptance-links"
          aria-label={t("legal_documents_label")}
        >
          <Link to="/legal/terms" className="legal-acceptance-link">
            {t("legal_document_terms_title")}
          </Link>
          <Link to="/legal/privacy" className="legal-acceptance-link">
            {t("legal_document_privacy_title")}
          </Link>
          <Link to="/legal/attributions" className="legal-acceptance-link">
            {t("legal_document_attributions_title")}
          </Link>
        </div>

        <p className="legal-acceptance-version">
          {t("legal_acceptance_current_version")}{" "}
          {user?.currentLegalVersion || t("unknown")}
        </p>

        <div className="legal-acceptance-consents">
          <div className="legal-acceptance-item">
            <div className="legal-acceptance-label">
              <input
                type="checkbox"
                checked={acceptTermsPrivacy}
                onChange={(event) => setAcceptTermsPrivacy(event.target.checked)}
              />
              <span>{renderTermsPrivacyLabel(localizedConsentTexts, t)}</span>
            </div>
            <p className="legal-acceptance-helper">
              {localizedConsentTexts.acceptTermsPrivacy.helper}
            </p>
          </div>

          <div className="legal-acceptance-item">
            <label className="legal-acceptance-label">
              <input
                type="checkbox"
                checked={acceptProfileVisibility}
                onChange={(event) =>
                  setAcceptProfileVisibility(event.target.checked)
                }
              />
              <span>{localizedConsentTexts.acceptProfileVisibility.label}</span>
            </label>
            <p className="legal-acceptance-helper">
              {localizedConsentTexts.acceptProfileVisibility.helper}
            </p>
          </div>
        </div>

        {error ? <p className="legal-acceptance-error">{error}</p> : null}

        <div className="legal-acceptance-actions">
          <button
            type="button"
            className="legal-acceptance-button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("saving") : t("legal_acceptance_continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
